import { Fragment, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { Breadcrumb, Container, Segment } from 'semantic-ui-react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { getBreadcrumbs } from '@plone/volto/actions/breadcrumbs/breadcrumbs';

import {
  getActiveLanguage,
  getLanguageHomePath,
  isLanguageHomePath,
} from '../../../../helpers/homepageUrl';
import { appLinkTo } from '../../../../helpers/appUrl';
import {
  getBreadcrumbApiPath,
  parseQuadreMedicMetgeRoute,
} from '../../../../helpers/breadcrumbApiPath';
import {
  findMetgeBySlug,
  useQuadreMedicPageData,
} from '../../../../helpers/quadreMedic';

const messages = defineMessages({
  home: {
    id: 'Home',
    defaultMessage: 'Home',
  },
  homeStart: {
    id: 'Breadcrumb home start',
    defaultMessage: 'Inici',
  },
  breadcrumbs: {
    id: 'Breadcrumbs',
    defaultMessage: 'Breadcrumbs',
  },
  controlpanel: {
    id: 'Site Setup',
    defaultMessage: 'Site Setup',
  },
});

const BreadcrumbDivider = () => (
  <span className="breadcrumb-divider" aria-hidden="true">
    ›
  </span>
);

const BreadcrumbsComponent = ({ pathname: _pathname }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { pathname: realPath } = useLocation();
  const breadcrumbApiPath = getBreadcrumbApiPath(realPath);
  const metgeRoute = parseQuadreMedicMetgeRoute(realPath);
  const { loading: quadreMedicLoading, quadreMedicList } =
    useQuadreMedicPageData(metgeRoute?.language);

  const controlpanelItems = [
    {
      url: '/controlpanel',
      title: intl.formatMessage(messages.controlpanel),
    },
  ];

  const reduxItems = useSelector(
    (state) =>
      realPath.startsWith('/controlpanel')
        ? controlpanelItems
        : state.breadcrumbs.items,
    shallowEqual,
  );
  const root = useSelector((state) => state.breadcrumbs.root);

  useEffect(() => {
    dispatch(getBreadcrumbs(breadcrumbApiPath));
  }, [dispatch, breadcrumbApiPath]);

  const items = useMemo(() => {
    if (realPath.startsWith('/controlpanel')) {
      return controlpanelItems;
    }
    if (!metgeRoute || quadreMedicLoading) {
      return reduxItems;
    }
    const metge = findMetgeBySlug(quadreMedicList, metgeRoute.metgeSlug);
    if (!metge?.CognomsNom) {
      return reduxItems;
    }
    return [
      ...reduxItems,
      { title: metge.CognomsNom, url: realPath.split('?')[0] },
    ];
  }, [
    realPath,
    metgeRoute,
    quadreMedicLoading,
    quadreMedicList,
    reduxItems,
    controlpanelItems,
  ]);

  if (isLanguageHomePath(realPath)) {
    return null;
  }

  const homePath =
    root || getLanguageHomePath(getActiveLanguage(realPath, intl.locale));

  return (
    <Fragment>
      <hr className="full-width-separator" />
      <Segment
        role="navigation"
        aria-label={intl.formatMessage(messages.breadcrumbs)}
        className="breadcrumbs"
        secondary
        vertical
      >
        <Container>
          <Breadcrumb>
            <Link
              to={homePath}
              className="section"
              title={intl.formatMessage(messages.home)}
            >
              {intl.formatMessage(messages.homeStart)}
            </Link>
            {items.map((item, index, items) => [
              <BreadcrumbDivider key={`divider-${item.url}`} />,
              index > 0 && index < items.length - 1 ? (
                <Link
                  key={item.url}
                  to={appLinkTo(item.url)}
                  className="section"
                >
                  {item.title}
                </Link>
              ) : (
                <Breadcrumb.Section key={item.url} active>
                  {item.title}
                </Breadcrumb.Section>
              ),
            ])}
          </Breadcrumb>
        </Container>
      </Segment>
    </Fragment>
  );
};

BreadcrumbsComponent.propTypes = {
  pathname: PropTypes.string.isRequired,
};

export default BreadcrumbsComponent;
